import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  ordersApi,
  type ApproveDispatchRequest,
  type ConfirmOrderWithAllocationRequest,
  type CreateOrderRequest,
  type ForecastRequestFilters,
  type PublishReadyDateRequest,
  type UpdateOrderRequest,
  type UpdateOrderStatusRequest,
  type CancelOrderRequest,
  type DispatchOrderRequest,
  type CalculateProductionStageRequest,
  type CreateOrderAmendmentRequest,
  type PreviewOrderAmendmentRequest,
  type SubmitOrderAmendmentRequest,
  type ApproveOrderAmendmentRequest,
  type RejectOrderAmendmentRequest,
  type ApplyOrderAmendmentRequest,
  type RefundTrayDepositRequest,
} from "../services/ordersApi";

export const useOrders = () => {
  return useQuery({
    queryKey: ["orders"],
    queryFn: ordersApi.getAllOrders,
  });
};

export const usePagedOrders = (params: {
  search?: string;
  pageNumber: number;
  pageSize: number;
}) => {
  return useQuery({
    queryKey: ["orders", "paged", params],
    queryFn: () => ordersApi.getPagedOrders(params),
    placeholderData: keepPreviousData,
  });
};

export const useOrderById = (id: string) => {
  return useQuery({
    queryKey: ["orders", id],
    queryFn: () => ordersApi.getOrderById(id),
    enabled: !!id,
  });
};

export const useCreateOrder = () => {
  return useMutation({
    mutationFn: (payload: CreateOrderRequest) =>
      ordersApi.createOrder(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
};

export const useUpdateOrder = () => {
  return useMutation({
    mutationFn: (payload: UpdateOrderRequest) =>
      ordersApi.updateOrder(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id, "tray-deposit-eligibility"] });
    },
  });
};

export const useUpdateOrderStatus = () => {
  return useMutation({
    mutationFn: (payload: UpdateOrderStatusRequest) =>
      ordersApi.updateOrderStatus(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id] });
    },
  });
};

export const useCancelOrder = () => {
  return useMutation({
    mutationFn: (payload: CancelOrderRequest) =>
      ordersApi.cancelOrder(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id] });
    },
  });
};

export const useRefundTrayDeposit = () => {
  return useMutation({
    mutationFn: ({
      orderId,
      payload,
    }: {
      orderId: string;
      payload: RefundTrayDepositRequest;
    }) => ordersApi.refundTrayDeposit(orderId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id, "tray-deposit-eligibility"] });
    },
  });
};

export const useTrayDepositEligibility = (orderId: string, enabled = true) => {
  return useQuery({
    queryKey: ["orders", orderId, "tray-deposit-eligibility"],
    queryFn: () => ordersApi.getTrayDepositEligibility(orderId),
    enabled: enabled && !!orderId,
  });
};

export const useOrdersByCustomerId = (customerId: string) => {
  return useQuery({
    queryKey: ["orders", "customer", customerId],
    queryFn: () => ordersApi.getOrdersByCustomerId(customerId),
    enabled: !!customerId,
  });
};

export const usePagedOrdersByCustomerId = (params: {
  customerId: string;
  pageNumber: number;
  pageSize: number;
}) => {
  return useQuery({
    queryKey: ["orders", "customer", params.customerId, "paged", params.pageNumber, params.pageSize],
    queryFn: () => ordersApi.getPagedOrdersByCustomerId(params),
    enabled: !!params.customerId,
    placeholderData: keepPreviousData,
  });
};

export const useCropGrowthTimelines = () => {
  return useQuery({
    queryKey: ["cropGrowthTimelines"],
    queryFn: ordersApi.getCropGrowthTimelines,
  });
};

export const useCalculateProductionStage = () => {
  return useMutation({
    mutationFn: (payload: CalculateProductionStageRequest) =>
      ordersApi.calculateProductionStage(payload),
  });
};

export const useOrdersReadyForSowing = () => {
  return useQuery({
    queryKey: ["orders", "ready-for-sowing"],
    queryFn: ordersApi.getOrdersReadyForSowing,
  });
};

export const usePagedOrdersReadyForSowing = (params: {
  search?: string;
  pageNumber: number;
  pageSize: number;
}) => {
  return useQuery({
    queryKey: ["orders", "ready-for-sowing", "paged", params],
    queryFn: () => ordersApi.getPagedOrdersReadyForSowing(params),
    placeholderData: keepPreviousData,
  });
};

export const usePagedDispatchableOrders = (params: {
  search?: string;
  pageNumber: number;
  pageSize: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["orders", "dispatchable", "paged", params.search ?? "", params.pageNumber, params.pageSize],
    queryFn: () =>
      ordersApi.getPagedDispatchableOrders({
        search: params.search,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      }),
    enabled: params.enabled ?? true,
    placeholderData: keepPreviousData,
  });
};

export const useOrdersWithActiveReservations = (enabled = true) => {
  return useQuery({
    queryKey: ["orders", "reserved"],
    queryFn: ordersApi.getOrdersWithActiveReservations,
    enabled,
  });
};

export const useOrdersByStatus = (status: number) => {
  return useQuery({
    queryKey: ["orders", "status", status],
    queryFn: () => ordersApi.getOrdersByStatus(status),
    enabled: status !== undefined && status !== null,
  });
};

export const useOrdersByDateRange = (startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ["orders", "date-range", startDate, endDate],
    queryFn: () => ordersApi.getOrdersByDateRange(startDate!, endDate!),
    enabled: !!startDate && !!endDate,
  });
};

export const useDispatchOrder = () => {
  return useMutation({
    mutationFn: (payload: DispatchOrderRequest) =>
      ordersApi.dispatchOrder(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id] });
      queryClient.invalidateQueries({ queryKey: ["orders", "status", 6] });
    },
  });
};

export const useOverrideConfirmOrder = () => {
  return useMutation({
    mutationFn: ({ orderId, notes }: { orderId: string; notes?: string }) =>
      ordersApi.requestPaymentThresholdOverride(orderId, notes ?? ""),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId] });
    },
  });
};

export const useWorkAuthorizationStatus = (orderId: string, enabled = true) => {
  return useQuery({
    queryKey: ["orders", orderId, "work-authorization-status"],
    queryFn: () => ordersApi.getWorkAuthorizationStatus(orderId),
    enabled: !!orderId && enabled,
  });
};

export const usePaymentThresholdOverrideQueue = (status?: string) => {
  return useQuery({
    queryKey: ["orders", "payment-threshold-overrides", "queue", status ?? "all"],
    queryFn: () => ordersApi.getPaymentThresholdOverrideQueue(status),
  });
};

export const useRevokePaymentThresholdOverride = () => {
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      ordersApi.revokePaymentThresholdOverride(id, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId, "payment-threshold-overrides"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "payment-threshold-overrides", "queue"] });
    },
  });
};

export const useOrderAmendments = (orderId: string) => {
  return useQuery({
    queryKey: ["orders", orderId, "amendments"],
    queryFn: () => ordersApi.getOrderAmendments(orderId),
    enabled: !!orderId,
  });
};

export const usePendingOrderAmendmentQueue = (pageNumber: number = 1, pageSize: number = 20) => {
  return useQuery({
    queryKey: ["orders", "amendments", "queue", pageNumber, pageSize],
    queryFn: () => ordersApi.getPendingOrderAmendmentQueue(pageNumber, pageSize),
  });
};

export const useCreateOrderAmendment = () => {
  return useMutation({
    mutationFn: ({ orderId, payload }: { orderId: string; payload: CreateOrderAmendmentRequest }) =>
      ordersApi.createOrderAmendment(orderId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId, "amendments"] });
    },
  });
};

export const usePreviewOrderAmendment = () => {
  return useMutation({
    mutationFn: ({ orderId, payload }: { orderId: string; payload: PreviewOrderAmendmentRequest }) =>
      ordersApi.previewOrderAmendment(orderId, payload),
  });
};

export const useSubmitOrderAmendment = () => {
  return useMutation({
    mutationFn: ({
      orderId,
      amendmentId,
      payload,
    }: {
      orderId: string;
      amendmentId: string;
      payload: SubmitOrderAmendmentRequest;
    }) => ordersApi.submitOrderAmendment(orderId, amendmentId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId, "amendments"] });
    },
  });
};

export const useApproveOrderAmendment = () => {
  return useMutation({
    mutationFn: ({
      orderId,
      amendmentId,
      payload,
    }: {
      orderId: string;
      amendmentId: string;
      payload: ApproveOrderAmendmentRequest;
    }) => ordersApi.approveOrderAmendment(orderId, amendmentId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.amendment.orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.amendment.orderId, "amendments"] });
    },
  });
};

export const useRejectOrderAmendment = () => {
  return useMutation({
    mutationFn: ({
      orderId,
      amendmentId,
      payload,
    }: {
      orderId: string;
      amendmentId: string;
      payload: RejectOrderAmendmentRequest;
    }) => ordersApi.rejectOrderAmendment(orderId, amendmentId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId, "amendments"] });
    },
  });
};

export const useApplyOrderAmendment = () => {
  return useMutation({
    mutationFn: ({
      orderId,
      amendmentId,
      payload,
    }: {
      orderId: string;
      amendmentId: string;
      payload: ApplyOrderAmendmentRequest;
    }) => ordersApi.applyOrderAmendment(orderId, amendmentId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.orderId, "amendments"] });
    },
  });
};

export const useOrdersAwaitingSalesReview = (params: {
  search?: string;
  pageNumber: number;
  pageSize: number;
}) => {
  return useQuery({
    queryKey: ["orders", "sales-review", "queue", params],
    queryFn: () => ordersApi.getSalesReviewQueue(params),
    placeholderData: keepPreviousData,
  });
};

export const useConfirmOrderWithAllocation = () => {
  return useMutation({
    mutationFn: ({
      orderId,
      payload,
    }: {
      orderId: string;
      payload: ConfirmOrderWithAllocationRequest;
    }) => ordersApi.confirmOrderWithAllocation(orderId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "sales-review", "queue"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id] });
    },
  });
};

export const usePublishReadyDate = () => {
  return useMutation({
    mutationFn: ({
      orderId,
      payload,
    }: {
      orderId: string;
      payload: PublishReadyDateRequest;
    }) => ordersApi.publishReadyDate(orderId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "sales-review", "queue"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id] });
    },
  });
};

export const useConfirmDates = () => {
  return useMutation({
    mutationFn: ({
      orderId,
      payload,
    }: {
      orderId: string;
      payload: { confirmedDeliveryDate?: string | null; confirmedPlantingDate?: string | null };
    }) => ordersApi.confirmDates(orderId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "sales-review", "queue"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id] });
    },
  });
};

export const usePushBackAgronomistOrder = () => {
  return useMutation({
    mutationFn: ({
      orderId,
      comment,
    }: {
      orderId: string;
      comment: string;
    }) => ordersApi.pushBackAgronomistOrder(orderId, { comment }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "sales-review", "queue"] });
      queryClient.invalidateQueries({ queryKey: ["orders", data.id] });
    },
  });
};

export const useForecastRequests = (filters: ForecastRequestFilters = {}) => {
  return useQuery({
    queryKey: ["orders", "sales-review", "forecast-requests", filters],
    queryFn: () => ordersApi.getForecastRequests(filters),
    placeholderData: keepPreviousData,
  });
};

export const useAcknowledgeForecastRequest = () => {
  return useMutation({
    mutationFn: (requestId: string) => ordersApi.acknowledgeForecastRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", "sales-review", "forecast-requests"] });
    },
  });
};

export const useSalesReviewAllocationPreview = (orderId: string, enabled = true) => {
  return useQuery({
    queryKey: ["orders", "sales-review", "allocation-preview", orderId],
    queryFn: () => ordersApi.getSalesReviewAllocationPreview(orderId),
    enabled: !!orderId && enabled,
  });
};

export const useDispatchReadinessQueue = (
  params: { search?: string; pageNumber: number; pageSize: number },
  enabled = true
) => {
  return useQuery({
    queryKey: ["orders", "dispatch-readiness", params],
    queryFn: () => ordersApi.getDispatchReadinessQueue(params),
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: enabled ? 30_000 : false,
  });
};

export const useApproveDispatch = () => {
  return useMutation({
    mutationFn: (payload: ApproveDispatchRequest) =>
      ordersApi.approveDispatch(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "dispatch-readiness"] });
      queryClient.invalidateQueries({ queryKey: ["dispatch"] });
      queryClient.invalidateQueries({ queryKey: ["customer-collections"] });
    },
  });
};
